import React, { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, Briefcase, ToggleLeft, ToggleRight } from "lucide-react";
import * as api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../ui/dialog";
import { EmptyState } from "../shared/EmptyState";
import { toast } from "sonner";

export function AdminPositions() {
    const { hasPermission, users } = useApp();
    const [positions, setPositions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [name, setName] = useState("");

    const getUserCountForPosition = useCallback((posId) => {
        return users.filter((u) => (u.position_ids || []).includes(posId) && u.is_active !== false).length;
    }, [users]);

    const loadPositions = async () => {
        try {
            const data = await api.getPositions();
            setPositions(data);
        } catch {
            toast.error("Erreur chargement des postes");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPositions();
    }, []);

    const handleSave = async () => {
        if (!name.trim()) return;
        try {
            if (editing) {
                await api.updatePosition(editing.id, { name: name.trim() });
                toast.success("Poste mis à jour");
            } else {
                await api.createPosition({ name: name.trim() });
                toast.success("Poste créé");
            }
            setDialogOpen(false);
            setEditing(null);
            setName("");
            loadPositions();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur");
        }
    };

    const handleToggle = async (pos) => {
        try {
            await api.updatePosition(pos.id, { is_active: !pos.is_active });
            toast.success(pos.is_active ? "Poste désactivé" : "Poste réactivé");
            loadPositions();
        } catch {
            toast.error("Erreur");
        }
    };

    const handleDelete = async (pos) => {
        if (!window.confirm(`Supprimer le poste "${pos.name}" ?`)) return;
        try {
            await api.deletePosition(pos.id);
            toast.success("Poste supprimé");
            loadPositions();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur");
        }
    };

    const openEdit = (pos) => {
        setEditing(pos);
        setName(pos.name);
        setDialogOpen(true);
    };

    const openCreate = () => {
        setEditing(null);
        setName("");
        setDialogOpen(true);
    };

    if (!hasPermission("admin")) return null;

    return (
        <div className="page-container" data-testid="admin-positions">
            <div className="page-header flex items-center justify-between">
                <div>
                    <h1 className="page-title">Postes / Fonctions</h1>
                    <p className="page-subtitle">{positions.length} poste(s) actif(s)</p>
                </div>
                <Button onClick={openCreate} data-testid="add-position-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter un poste
                </Button>
            </div>

            {positions.length === 0 && !loading ? (
                <div className="card">
                    <EmptyState
                        icon={Briefcase}
                        title="Aucun poste"
                        description="Créez des postes pour les assigner à vos collaborateurs"
                    />
                </div>
            ) : (
                <div className="card">
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Nom du poste</th>
                                    <th>Utilisateurs</th>
                                    <th>Statut</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {positions.map((pos) => {
                                    const userCount = getUserCountForPosition(pos.id);
                                    return (
                                    <tr key={pos.id} className={!pos.is_active ? "opacity-50" : ""} data-testid={`position-row-${pos.id}`}>
                                        <td>
                                            <div className="flex items-center gap-2">
                                                <Briefcase className="w-4 h-4 text-gray-400" />
                                                <span className="font-medium">{pos.name}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="text-sm text-gray-500" data-testid={`position-count-${pos.id}`}>
                                                {userCount} utilisateur{userCount !== 1 ? "s" : ""}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${pos.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                                                {pos.is_active ? "Actif" : "Inactif"}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="flex items-center gap-1 justify-end">
                                                <Button variant="ghost" size="sm" onClick={() => openEdit(pos)} data-testid={`edit-position-${pos.id}`}>
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button variant="ghost" size="sm" onClick={() => handleToggle(pos)} data-testid={`toggle-position-${pos.id}`}>
                                                    {pos.is_active ? <ToggleRight className="w-4 h-4 text-green-600" /> : <ToggleLeft className="w-4 h-4 text-gray-400" />}
                                                </Button>
                                                <Button variant="ghost" size="sm" onClick={() => handleDelete(pos)} data-testid={`delete-position-${pos.id}`}>
                                                    <Trash2 className="w-4 h-4 text-red-500" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>{editing ? "Modifier le poste" : "Nouveau poste"}</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Nom du poste (ex: Chef de chantier)"
                            onKeyDown={(e) => e.key === "Enter" && handleSave()}
                            autoFocus
                            data-testid="position-name-input"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                        <Button onClick={handleSave} disabled={!name.trim()} data-testid="save-position-btn">
                            {editing ? "Enregistrer" : "Créer"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
